import React from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AppLayout from "./components/Layout/AppLayout";
import LandingPage from "./pages/LandingPage/LandingPage";
import LoginPage from "./pages/LoginPage/LoginPage";
import ForgotPasswordPage from "./pages/LoginPage/ForgotPasswordPage";
import ResetPasswordPage from "./pages/LoginPage/ResetPasswordPage";
import RegisterPage from "./pages/RegisterPage/RegisterPage";
import HomePage from "./pages/HomePage/HomePage";
import SearchPage from "./pages/SearchPage/SearchPage";
import EditProfilePage from "./pages/EditProfilePage/EditProfilePage";
import PublicProfilePage from "./pages/PublicProfilePage/PublicProfilePage";
import ManageArtworksPage from "./pages/ManageArtworksPage/ManageArtworksPage";
import BecomeArtistPage from "./pages/BecomeArtistPage/BecomeArtistPage";
import SettingsPage from "./pages/SettingsPage/SettingsPage";
import ArtworkGalleryPage from "./pages/ArtworkGalleryPage/ArtworkGalleryPage";
import ArtworkDetailPage from "./pages/ArtworkDetailPage/ArtworkDetailPage";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";

// Admin Pages
// Admin Pages
import AdminLayout from './pages/AdminLayout/AdminLayout';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import AdminUsersPage from './pages/AdminUsersPage/AdminUsersPage';
import AdminAccessControl from './pages/AdminAccessControl/AdminAccessControl';
import AdminVerificationPage from './pages/AdminVerificationPage/AdminVerificationPage';
import AdminEmailPage from './pages/AdminEmailPage/AdminEmailPage';
import QuotesPage from './pages/ArtistQuotesPage/QuotesPage';
import InstagramCallback from './pages/LoginPage/InstagramCallback';
import { UpgradeSuccess, UpgradeError } from './pages/UpgradeOutcome/UpgradeOutcome';
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InstallPWA from './components/InstallPWA/InstallPWA';

function App() {
  return (
    <AuthProvider>
      <InstallPWA />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<InstagramCallback />} />
        <Route path="/upgrade/success" element={<UpgradeSuccess />} />
        <Route path="/upgrade/error" element={<UpgradeError />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Admin Routes - Protected by Permission */}
        <Route path="/admin" element={
          <ProtectedRoute permission="sys.admin">
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />

          <Route path="users" element={<AdminUsersPage />} />
          <Route path="access-control" element={<AdminAccessControl />} />
          <Route path="verification" element={<AdminVerificationPage />} />
          <Route path="email" element={<AdminEmailPage />} />
        </Route>

        {/* App Layout Routes */}
        <Route element={<AppLayout><Outlet /></AppLayout>}>
          {/* Protected Routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/become-artist"
            element={
              <ProtectedRoute>
                <BecomeArtistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/explore"
            element={
              <SearchPage />
            }
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute permission="profile.edit">
                <EditProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artworks/manage"
            element={
              <ProtectedRoute permission="artwork.manage">
                <ManageArtworksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artist/quotes"
            element={
              <ProtectedRoute permission="quote.manage">
                <QuotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-quotes"
            element={
              <ProtectedRoute permission="quote.view.own">
                <QuotesPage />
              </ProtectedRoute>
            }
          />

          {/* Public Artwork Routes */}
          <Route path="/artworks/:id" element={<ArtworkDetailPage />} />
          <Route path="/profile/:username/artworks" element={<ArtworkGalleryPage />} />

        </Route>

        {/* Dynamic Public Route - Must be last to avoid conflicts */}
        <Route path="profile/:username" element={
          <ProfileRouteWrapper />
        } />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
    </AuthProvider>
  );
}

const ProfileRouteWrapper = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? (
    <AppLayout noPadding={true}>
      <PublicProfilePage />
    </AppLayout>
  ) : (
    <PublicProfilePage />
  );
};

export default App;
